/** Helpers to add an event to Google / Outlook / Apple (iCal) calendars. */

export interface CalendarEvent {
  title: string
  description?: string
  location?: string
  /** ISO start datetime. */
  start: string
  /** ISO end datetime. */
  end: string
  /** Link back to the event page (added to the calendar entry body). */
  url?: string
}

/** `YYYYMMDDTHHMMSSZ` (UTC) — the compact form Google/iCal expect. */
function toCompactUTC(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function bodyText(e: CalendarEvent): string {
  return [e.description, e.url].filter(Boolean).join('\n\n')
}

export function googleCalendarUrl(e: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates: `${toCompactUTC(e.start)}/${toCompactUTC(e.end)}`,
    details: bodyText(e),
    location: e.location ?? '',
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

export function outlookCalendarUrl(e: CalendarEvent): string {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: e.title,
    startdt: new Date(e.start).toISOString(),
    enddt: new Date(e.end).toISOString(),
    body: bodyText(e),
    location: e.location ?? '',
  })
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`
}

/** A valid VCALENDAR string for an Apple/Outlook `.ics` download. */
export function icsContent(e: CalendarEvent): string {
  const esc = (s: string) =>
    s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
  const stamp = toCompactUTC(e.start)
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Gamerplug//Tournaments//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${stamp}-${esc(e.title).slice(0, 32)}@gamerplug.app`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${toCompactUTC(e.start)}`,
    `DTEND:${toCompactUTC(e.end)}`,
    `SUMMARY:${esc(e.title)}`,
    bodyText(e) ? `DESCRIPTION:${esc(bodyText(e))}` : '',
    e.location ? `LOCATION:${esc(e.location)}` : '',
    e.url ? `URL:${e.url}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean)
  return lines.join('\r\n')
}

/** Trigger a browser download of the event as an `.ics` file. */
export function downloadIcs(e: CalendarEvent, filename: string): void {
  const blob = new Blob([icsContent(e)], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
